import React from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const MIN_SEGMENT_PCT = 6;

const redistribuirComPiso = (valores, piso = MIN_SEGMENT_PCT) => {
  const validos = valores
    .map((v, i) => ({ v, i }))
    .filter(({ v }) => typeof v === "number" && v > 0);
  if (!validos.length) return valores;

  const abaixo = validos.filter(({ v }) => v < piso);
  if (!abaixo.length) return valores;

  const acima = validos.filter(({ v }) => v >= piso);
  const deficit = abaixo.reduce((soma, { v }) => soma + (piso - v), 0);
  const somaAcima = acima.reduce((soma, { v }) => soma + v, 0);
  const fator = somaAcima > 0 ? Math.max(0, (somaAcima - deficit) / somaAcima) : 0;

  const ajustado = [...valores];
  for (const { i } of abaixo) ajustado[i] = piso;
  for (const { i, v } of acima) ajustado[i] = v * fator;
  return ajustado;
};

const hexToRgba = (hex, alpha) => {
  if (typeof hex !== "string") return hex;
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean;
  if (full.length !== 6) return hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const SelfServicesChart = ({ chartData, title, emptyMessage }) => {
  if (emptyMessage) {
    return (
      <div className="container py-2">
        <div className="rounded-md shadow bg-white p-6">
          {!!title && (
            <h2 className="mb-4 text-lg font-bold text-[#0A2E73]">{title}</h2>
          )}
          <div style={{ height: 280 }} className="flex items-center justify-center">
            <p className="text-center text-sm font-semibold text-[#8D96A8]">{emptyMessage}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!chartData?.labels?.length) {
    return null;
  }

  const originalDatasets = chartData.datasets;
  const fadedFlags = chartData.fadedFlags || [];

  const numBarras = chartData.labels.length;
  const valoresPorBarraAjustados = Array.from({ length: numBarras }, (_, barIndex) =>
    redistribuirComPiso(originalDatasets.map((ds) => ds.data?.[barIndex] ?? null))
  );

  const datasetsWithStyle = originalDatasets.map((ds, i) => {
    const isTop = i === originalDatasets.length - 1;
    const baseColor = ds.backgroundColor;
    const backgroundColor = fadedFlags.length
      ? fadedFlags.map((faded) => (faded ? hexToRgba(baseColor, 0.35) : baseColor))
      : baseColor;
    const adjustedData = valoresPorBarraAjustados.map((valores) => valores[i]);
    const common = {
      ...ds,
      data: adjustedData,
      backgroundColor,
      barPercentage: 0.5,
      categoryPercentage: 0.8,
    };
    return isTop
      ? { ...common, borderRadius: { topLeft: 5, topRight: 5 }, borderSkipped: "bottom" }
      : common;
  });

  const displayData = {
    ...chartData,
    datasets: datasetsWithStyle,
  };

  // Datasets que trazem `quantidades` exibem o número absoluto (inteiro, sem "%");
  // os demais continuam mostrando o percentual da fatia.
  const lerValor = (datasetIndex, dataIndex, percentualPadrao = 0) => {
    const ds = originalDatasets[datasetIndex];
    return {
      percentual: ds?.data?.[dataIndex] ?? percentualPadrao,
      quantidade: ds?.quantidades?.[dataIndex],
    };
  };

  const formatarValor = ({ percentual, quantidade }) =>
    quantidade == null
      ? `${Number(percentual).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
      : Math.round(quantidade).toLocaleString("pt-BR");

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20 },
    },
    plugins: {
      legend: {
        position: "right",
        labels: {
          font: { size: 12, weight: "bold" },
          padding: 16,
          usePointStyle: true,
          pointStyle: "circle",
          pointStyleWidth: 14,
          boxHeight: 10,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ` ${ctx.dataset.label}: ${formatarValor(lerValor(ctx.datasetIndex, ctx.dataIndex, ctx.raw))}`,
        },
      },
      datalabels: {
        color: "#fff",
        font: { size: 11, weight: "bold" },
        formatter: (_value, ctx) => {
          const valor = lerValor(ctx.datasetIndex, ctx.dataIndex);
          return valor.percentual > 0 ? formatarValor(valor) : "";
        },
        anchor: "center",
        align: "center",
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11, weight: "bold" }, color: "#1A2B49" },
      },
      y: {
        stacked: true,
        display: false,
        max: 100,
        grid: { color: "#f0f0f0" },
        ticks: {
          callback: (v) => `${v}`,
          font: { size: 11 },
          color: "#1A2B49",
        },
      },
    },
  };

  return (
    <div className="container py-2">
      <div className="rounded-md shadow bg-white p-6">
        {!!title && (
          <h2 className="mb-4 text-lg font-bold text-[#0A2E73]">{title}</h2>
        )}
        <div style={{ height: 280 }}>
          <Bar data={displayData} options={options} />
        </div>
      </div>
    </div>
  );
};

const MemoSelfServicesChart = React.memo(SelfServicesChart);
export default MemoSelfServicesChart;
